import { DateTime } from "luxon";
import { MODERATION_REASONS } from "../../../RecipientDetail/moderationConstants";
import { PATH_TYPES } from "../../../RecipientDetail/sanctionConstants";
import { INFORMATIONS_COMPLEMENTAIRES_LABELS } from "../../EpFormV2/EpRecipientsV2/informationsComplementairesConstants";
import {
  getDetectionLabel,
  getDetectionStyle,
  getReasonLabel,
  PRESENCE_OPTIONS_SUPPRESSION,
  SectionHeader,
} from "./DecisionModalHelpers";

const DecisionModalContext = ({
  inscription,
  sanctionStyle,
  presence,
  setPresence,
  readOnly,
  isSuspension,
}) => {
  return (
    <div
      className="col-12 col-lg-5 p-4"
      style={{ borderRight: "1px solid #e2e8f0", background: "#fafbfc" }}
    >
      {/* Présence — masquée pour les SUSPENSION (non convoqués automatiquement) */}
      {!isSuspension && (
        <>
          <SectionHeader
            icon="fa-user-check"
            iconBg="#6366f1"
            title="Présence"
            subtitle="Renseignez la présence de l'allocataire"
          />
          <div className="d-flex flex-wrap mb-4" style={{ gap: 8 }}>
            {PRESENCE_OPTIONS_SUPPRESSION.map((opt) => {
              const isActive = presence === opt.id;
              const isAbsentType =
                opt.id === "ABSENT" || opt.id === "ABSENT_OBSERVATIONS_ECRITES";

              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => !readOnly && setPresence(opt.id)}
                  disabled={readOnly}
                  className="btn btn-sm"
                  style={{
                    background: isActive
                      ? isAbsentType
                        ? "#fee2e2"
                        : "#dcfce7"
                      : "#fff",
                    color: isActive
                      ? isAbsentType
                        ? "#991b1b"
                        : "#166534"
                      : "#475569",
                    border: isActive
                      ? `2px solid ${isAbsentType ? "#ef4444" : "#22c55e"}`
                      : "1px solid #e2e8f0",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 13,
                    borderRadius: 8,
                    padding: "6px 14px",
                    transition: "all 0.15s ease",
                  }}
                >
                  {isActive && (
                    <i
                      className={`fa ${isAbsentType ? "fa-times" : "fa-check"} mr-1`}
                      style={{ fontSize: 11 }}
                    />
                  )}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Contexte inscription */}
      <SectionHeader
        icon="fa-tags"
        iconBg="#0ea5e9"
        title="Contexte de l'inscription"
      />
      <div
        className="p-3 rounded mb-4"
        style={{ background: "#fff", border: "1px solid #e2e8f0" }}
      >
        {/* Type de sanction */}
        <div className="mb-3">
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Type de sanction
          </span>
          <div className="mt-1">
            <span
              className="d-inline-flex align-items-center px-2 py-1 rounded"
              style={{
                background: sanctionStyle.bg,
                color: sanctionStyle.color,
                fontSize: 13,
                fontWeight: 700,
                gap: 5,
              }}
            >
              <i
                className={`fa ${sanctionStyle.icon}`}
                style={{ fontSize: 12 }}
              />
              {sanctionStyle.label}
            </span>
            {inscription.detectionType && (
              <span
                className="ml-1 d-inline-flex align-items-center px-2 py-1 rounded"
                style={{
                  background: getDetectionStyle(inscription.detectionType).bg,
                  color: getDetectionStyle(inscription.detectionType).color,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {getDetectionLabel(inscription.detectionType)}
              </span>
            )}
          </div>
        </div>

        {/* Motif principal */}
        <div
          className="mb-3"
          style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Motif principal
          </span>
          <div
            className="mt-1"
            style={{
              fontSize: 13,
              color: "#334155",
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            {getReasonLabel(inscription.motif)}
          </div>
        </div>

        {/* Motifs secondaires (modération) */}
        {inscription.secondaryReasons &&
          inscription.secondaryReasons.length > 0 && (
            <div
              className="mb-3"
              style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Motifs de modération
              </span>
              <div className="d-flex flex-wrap mt-1" style={{ gap: 6 }}>
                {inscription.secondaryReasons.map((reasonId) => (
                  <span
                    key={reasonId}
                    className="d-inline-flex align-items-center px-2 py-1 rounded"
                    style={{
                      fontSize: 12,
                      fontWeight: 400,
                    }}
                  >
                    <i className="fa fa-tag mr-1" style={{ fontSize: 10 }} />
                    {MODERATION_REASONS[reasonId]?.label || reasonId}
                  </span>
                ))}
              </div>
            </div>
          )}

        {/* Informations complémentaires */}
        {inscription.additionalInformations?.length > 0 && (
          <div
            className="mb-3"
            style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Informations complémentaires
            </span>
            <div className="d-flex flex-wrap mt-1" style={{ gap: 6 }}>
              {inscription.additionalInformations.map((info) => (
                <span
                  key={info}
                  className="d-inline-flex align-items-center px-2 py-1 rounded"
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    background: "#f0f9ff",
                    color: "#0369a1",
                    border: "1px solid #bae6fd",
                  }}
                >
                  <i
                    className="fa fa-info-circle mr-1"
                    style={{ fontSize: 10 }}
                  />
                  {INFORMATIONS_COMPLEMENTAIRES_LABELS[info] || info}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Parcours */}
        {inscription.pathType && (
          <div
            className="mb-3"
            style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Parcours
            </span>
            <div className="mt-1">
              <span
                className="d-inline-flex align-items-center px-2 py-1 rounded"
                style={{
                  background: "#ede9fe",
                  color: "#5b21b6",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <i className="fa fa-road mr-1" style={{ fontSize: 10 }} />
                {Object.values(PATH_TYPES).find(
                  (p) => p.id === inscription.pathType,
                )?.label || inscription.pathType}
              </span>
            </div>
          </div>
        )}

        {/* Organisme de saisine */}
        {inscription.organizationOrigin && (
          <div
            className="mb-3"
            style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Organisme de saisine
            </span>
            <div
              className="mt-1"
              style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}
            >
              {inscription.organizationOrigin}
            </div>
          </div>
        )}

        {/* Grossesse */}
        {inscription.pregnancy && (
          <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
            <span
              className="d-inline-flex align-items-center px-2 py-1 rounded"
              style={{
                background: "#fce7f3",
                color: "#9d174d",
                fontSize: 12,
                fontWeight: 700,
                gap: 5,
              }}
            >
              <i className="fa fa-heart" style={{ fontSize: 10 }} />
              Grossesse déclarée
            </span>
          </div>
        )}
      </div>

      {/* Historique EP */}
      <SectionHeader
        icon="fa-history"
        iconBg="#f59e0b"
        title="Antécédents EP"
        subtitle={
          inscription.epHistory && inscription.epHistory.length > 0
            ? `${inscription.epHistory.length} passage(s) précédent(s)`
            : "Aucun antécédent"
        }
      />
      {inscription.epHistory && inscription.epHistory.length > 0 ? (
        <div className="d-flex flex-column mb-4" style={{ gap: 8 }}>
          {inscription.epHistory.map((ant, idx) => (
            <div
              key={idx}
              className="p-3 rounded"
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-1">
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#92400e",
                  }}
                >
                  EP du {DateTime.fromISO(ant.epDate).toFormat("dd/MM/yyyy")}
                  {ant.notificationDate && (
                    <span
                      style={{
                        fontWeight: 400,
                        color: "#a16207",
                        marginLeft: 4,
                      }}
                    >
                      — décision le{" "}
                      {DateTime.fromISO(ant.notificationDate).toFormat(
                        "dd/MM/yyyy",
                      )}
                    </span>
                  )}
                </span>
                {ant.detectionType && (
                  <span
                    className="px-2 py-1 rounded"
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      background: getDetectionStyle(ant.detectionType).bg,
                      color: getDetectionStyle(ant.detectionType).color,
                    }}
                  >
                    {getDetectionLabel(ant.detectionType)}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#475569",
                  lineHeight: 1.5,
                }}
              >
                {ant.type && (
                  <>
                    <strong>Type:</strong> {ant.type}
                    <br />
                  </>
                )}
                {ant.mainReason && (
                  <>
                    <strong>Motif:</strong> {getReasonLabel(ant.mainReason)}
                    <br />
                  </>
                )}
                <strong>Décision:</strong> {ant.decision}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="p-3 rounded mb-4 text-center"
          style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}
        >
          <i
            className="fa fa-check-circle mr-2"
            style={{ color: "#22c55e", fontSize: 14 }}
          />
          <span style={{ fontSize: 13, color: "#166534", fontWeight: 500 }}>
            Aucun antécédent EP connu pour cet allocataire
          </span>
        </div>
      )}
    </div>
  );
};

export default DecisionModalContext;
